import {
  Button,
  Checkbox,
  CloseIcon,
  Drawer,
  DatePicker,
  Radio,
  SelectSearch,
  Text,
} from '@mercai/clever'
import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { useUserList } from '@/services'

import styles from './drawer-filter.module.css'

import { QuotationRequestOriginEnum, QuotationRequestStatusEnum } from '@/types'
import { DrawerFilterProps } from './drawer-filter.type'

export const QuotationRequestDrawerFilter = ({
  isOpen,
  onHandleOpen,
  filter,
  onHandleFilter,
}: DrawerFilterProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenQuotationRequestListV2')

  const [usersSearchTerm, setUsersSearchTerm] = useState<string>('')
  const [selectedKam, setSelectedKam] = useState<{
    label: string
    value: string
  }>()

  const { data: dataUsers, isLoading: isLoadingUsersSearch } = useUserList({
    params: {
      limit: 100,
      page: 1,
      isStaff: 'true',
      search: usersSearchTerm,
    },
  })

  const getStringFilterValue = (key: string) => {
    return filter?.[key] as string
  }

  const onChangeStringFilterValue = (key: string, value: string) => {
    onHandleFilter({ ...filter, [key]: value })
  }

  const getBooleanOfMultiValue = (key: string, value: string): boolean => {
    const values = (filter[key] as string)?.split(',') || []
    return values.includes(value)
  }

  const onChangeBooleanOfMultiValue = (key: string, value: string) => {
    let values = (filter[key] as string)?.split(',') || []
    values = values.filter((item) => !!item)

    let newValues = ''

    if (values.includes(value)) {
      newValues = values.filter((item) => item !== value).join(',')
    } else {
      newValues = [...values, value].join(',')
    }

    onHandleFilter({ ...filter, [key]: newValues })
  }

  const getBooleanOfSingleValue = (key: string, value: string): boolean => {
    return filter[key] === value
  }

  const onChangeBooleanOfSingleValue = (key: string, value: string) => {
    onHandleFilter({ ...filter, [key]: value })
  }

  const onApplyIsAutomated = (key: string, value: string) => {
    if (value === '' && key === 'both') {
      onHandleFilter({ ...filter, is_automated: '', is_ai_automated: '' })

      return
    }

    const reverseKeys = {
      is_automated: 'is_ai_automated',
      is_ai_automated: 'is_automated',
    }

    const reverseKey = reverseKeys[key as keyof typeof reverseKeys]

    onHandleFilter({
      ...filter,
      [key]: value,
      [reverseKey]: '',
    })
  }

  const isApplyIsAutomated = (key: string, value: string) => {
    const reverseKeys = {
      is_automated: 'is_ai_automated',
      is_ai_automated: 'is_automated',
    }

    if (key === 'both') {
      return filter.is_ai_automated === value && filter.is_automated === value
    }

    const reverseKey = reverseKeys[key as keyof typeof reverseKeys]

    return filter[key] === value || filter[reverseKey] === value
  }

  const userOptions = useMemo(() => {
    const usersWithName =
      dataUsers?.data?.results?.filter((user) => user?.name) || []

    const mappedUsers = usersWithName.map((user) => ({
      label: user.name,
      value: `${user.id}`,
    }))

    const options = [
      { label: t('quotationRequestTableFilterKAMNoFilledLabel'), value: '' },
      ...mappedUsers,
    ]

    return options.sort((a, b) => a.label.localeCompare(b.label))
  }, [dataUsers, t])

  return (
    <Drawer isOpen={isOpen} onHandleOpen={onHandleOpen}>
      <div className={styles.header}>
        <Text variant="title-large" className={styles['header-title']}>
          {t('quotationRequestTableFilterTitle')}
        </Text>

        <button
          className={styles['header-button']}
          type="button"
          onClick={onHandleOpen}
        >
          <CloseIcon />
        </button>
      </div>

      <div className={styles.content}>
        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('quotationRequestTableFilterCreatedAtLabel')}
          </Text>

          <div className={styles['content-filter-row']}>
            <DatePicker
              value={getStringFilterValue('createdAtAfter')}
              onChange={(value) =>
                onChangeStringFilterValue('createdAtAfter', value || '')
              }
              placeholder="dd/mm/aaaa"
            />

            <Text>-</Text>

            <DatePicker
              value={getStringFilterValue('createdAtBefore')}
              onChange={(value) =>
                onChangeStringFilterValue('createdAtBefore', value || '')
              }
              placeholder="dd/mm/aaaa"
            />
          </div>
        </div>
        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('quotationRequestTableFilterKAMLabel')}
          </Text>
          <SelectSearch<{ label: string; value: string }>
            dataSearch={userOptions}
            isLoadingSearch={isLoadingUsersSearch}
            onSearch={setUsersSearchTerm}
            onSelectItem={({ label = '', value = '' }) => {
              setSelectedKam({ label, value })
              onHandleFilter({ ...filter, kam: value })
            }}
            selectedItem={selectedKam}
            translations={{
              inputSearchPlaceholder: t('quotationRequestFilterKAMPlaceholder'),
              noResults: t('quotationRequestFilterKAMNoResults'),
            }}
          />
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('quotationRequestTableFilterStateLabel')}
          </Text>
          <div className={styles['content-filter-column']}>
            {Object.keys(QuotationRequestStatusEnum)?.map((status) => (
              <Checkbox
                value={getBooleanOfMultiValue('status', status)}
                onChange={() => onChangeBooleanOfMultiValue('status', status)}
              >
                {tEnums(`quotation_request_status_${status}`)}
              </Checkbox>
            ))}
          </div>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('quotationRequestTableFilterOriginLabel')}
          </Text>

          <div className={styles['content-filter-column']}>
            {Object.keys(QuotationRequestOriginEnum)?.map((origin) => (
              <Checkbox
                value={getBooleanOfMultiValue('origin', origin)}
                onChange={() => onChangeBooleanOfMultiValue('origin', origin)}
              >
                {tEnums(`quotation_request_origin_${origin}`)}
              </Checkbox>
            ))}
          </div>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('quotationRequestTableFilterMoscowLabel')}
          </Text>

          <div className={styles['content-filter-column']}>
            <Checkbox
              value={getBooleanOfMultiValue('moscow', 'must')}
              onChange={() => onChangeBooleanOfMultiValue('moscow', 'must')}
            >
              {t('quotationRequestTableFilterMoscowMW')}
            </Checkbox>

            <Checkbox
              value={getBooleanOfMultiValue('moscow', 'should')}
              onChange={() => onChangeBooleanOfMultiValue('moscow', 'should')}
            >
              {t('quotationRequestTableFilterMoscowSW')}
            </Checkbox>

            <Checkbox
              value={getBooleanOfMultiValue('moscow', 'could')}
              onChange={() => onChangeBooleanOfMultiValue('moscow', 'could')}
            >
              {t('quotationRequestTableFilterMoscowCW')}
            </Checkbox>

            <Checkbox
              value={getBooleanOfMultiValue('moscow', 'wont')}
              onChange={() => onChangeBooleanOfMultiValue('moscow', 'wont')}
            >
              {t('quotationRequestTableFilterMoscowWW')}
            </Checkbox>
          </div>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('quotationRequestTableFilterRootCategoryLabel')}
          </Text>

          <div className={styles['content-filter-column']}>
            <Radio
              value={getBooleanOfSingleValue('rootCategory', 'Metais')}
              onChange={() =>
                onChangeBooleanOfSingleValue('rootCategory', 'Metais')
              }
            >
              {t('quotationRequestTableFilterRootCategoryMetales')}
            </Radio>

            <Radio
              value={getBooleanOfSingleValue('rootCategory', 'ResPlas')}
              onChange={() =>
                onChangeBooleanOfSingleValue('rootCategory', 'ResPlas')
              }
            >
              {t('quotationRequestTableFilterRootCategoryResPlas')}
            </Radio>

            <Radio
              value={getBooleanOfSingleValue('rootCategory', 'Uncategorized')}
              onChange={() =>
                onChangeBooleanOfSingleValue('rootCategory', 'Uncategorized')
              }
            >
              {t('quotationRequestTableFilterRootCategoryUncategorized')}
            </Radio>

            <Radio
              value={getBooleanOfSingleValue('rootCategory', '')}
              onChange={() => onChangeBooleanOfSingleValue('rootCategory', '')}
            >
              {t('quotationRequestTableFilterRootCategoryUndefined')}
            </Radio>
          </div>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('quotationRequestTableFilterTrafficSource')}
          </Text>

          <div className={styles['content-filter-column']}>
            <Checkbox
              value={getBooleanOfMultiValue('traffic_source', 'paid')}
              onChange={() =>
                onChangeBooleanOfMultiValue('traffic_source', 'paid')
              }
            >
              {t('quotationRequestTableFilterTrafficSourcePaid')}
            </Checkbox>

            <Checkbox
              value={getBooleanOfMultiValue('traffic_source', 'organic')}
              onChange={() =>
                onChangeBooleanOfMultiValue('traffic_source', 'organic')
              }
            >
              {t('quotationRequestTableFilterTrafficSourceOrganic')}
            </Checkbox>

            <Checkbox
              value={getBooleanOfMultiValue('traffic_source', 'referral')}
              onChange={() =>
                onChangeBooleanOfMultiValue('traffic_source', 'referral')
              }
            >
              {t('quotationRequestTableFilterTrafficSourceReferral')}
            </Checkbox>

            <Checkbox
              value={getBooleanOfMultiValue('traffic_source', 'direct')}
              onChange={() =>
                onChangeBooleanOfMultiValue('traffic_source', 'direct')
              }
            >
              {t('quotationRequestTableFilterTrafficSourceDirect')}
            </Checkbox>

            <Checkbox
              value={getBooleanOfMultiValue('traffic_source', 'unknown')}
              onChange={() =>
                onChangeBooleanOfMultiValue('traffic_source', 'unknown')
              }
            >
              {t('quotationRequestTableFilterTrafficSourceUnknown')}
            </Checkbox>
          </div>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('quotationRequestTableFilterType')}
          </Text>

          <div className={styles['content-filter-column']}>
            <Checkbox
              value={getBooleanOfMultiValue('buyer_profile__type', 'inbound')}
              onChange={() =>
                onChangeBooleanOfMultiValue('buyer_profile__type', 'inbound')
              }
            >
              {t('quotationRequestTableFilterTypeInbound')}
            </Checkbox>

            <Checkbox
              value={getBooleanOfMultiValue('buyer_profile__type', 'outbound')}
              onChange={() =>
                onChangeBooleanOfMultiValue('buyer_profile__type', 'outbound')
              }
            >
              {t('quotationRequestTableFilterTypeOutbound')}
            </Checkbox>

            <Checkbox
              value={getBooleanOfMultiValue(
                'buyer_profile__type',
                'inbound_reactivation',
              )}
              onChange={() =>
                onChangeBooleanOfMultiValue(
                  'buyer_profile__type',
                  'inbound_reactivation',
                )
              }
            >
              {t('quotationRequestTableFilterTypeInboundReactivation')}
            </Checkbox>

            <Checkbox
              value={getBooleanOfMultiValue(
                'buyer_profile__type',
                'outbound_reactivation',
              )}
              onChange={() =>
                onChangeBooleanOfMultiValue(
                  'buyer_profile__type',
                  'outbound_reactivation',
                )
              }
            >
              {t('quotationRequestTableFilterTypeOutboundReactivation')}
            </Checkbox>
          </div>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('quotationRequestTableFilterIsAutomatedLabel')}
          </Text>

          <div className={styles['content-filter-column']}>
            <Radio
              value={getBooleanOfSingleValue('is_automated', 'true')}
              onChange={() => onApplyIsAutomated('is_automated', 'true')}
            >
              {t('quotationRequestTableFilterIsAutomatedYes')}
            </Radio>

            <Radio
              value={getBooleanOfSingleValue('is_ai_automated', 'true')}
              onChange={() => onApplyIsAutomated('is_ai_automated', 'true')}
            >
              {t('quotationRequestTableFilterIsAiAutomatedYes')}
            </Radio>

            <Radio
              value={isApplyIsAutomated('both', '')}
              onChange={() => onApplyIsAutomated('both', '')}
            >
              {t('quotationRequestTableFilterIsAutomatedAll')}
            </Radio>
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <Button variant="secondary" onClick={onHandleOpen}>
          {t('quotationRequestTableFilterButtonBack')}
        </Button>
      </div>
    </Drawer>
  )
}
